import React, { useState, useEffect, useRef } from "react";
import { CURRENCIES, getCurrencySymbol } from "./currencies";
import { Button } from "../../../_helpers/components/Button";

const AddNotesFrais = ({
    open,
    onClose,
    onSubmit,
    onValidate,
    onRefuse,
    note = null,
    noteLoading = false,
    editable = true,
}) => {
    const [form, setForm] = useState({
        date: "",
        type: "Repas",
        montant: "",
        currency: "EUR",
        fichiers: [],
        commentaire: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingDoc, setLoadingDoc] = useState(null);
    const [showRefuseForm, setShowRefuseForm] = useState(false);
    const [refuseComment, setRefuseComment] = useState("");
    const fileInputRef = useRef(null);

    useEffect(() => {
        setShowRefuseForm(false);
        setRefuseComment("");
        setForm({
            date: "",
            type: "Repas",
            montant: "",
            currency: "EUR",
            fichiers: [],
            commentaire: "",
        });
        if (note) {
            const dateVal =
                typeof note.date === "string" && note.date.length >= 10
                    ? note.date.slice(0, 10)
                    : note.date || "";
            setForm({
                date: dateVal,
                type: note.type || note.expense_type || "Repas",
                montant: note.montant ?? note.amount ?? "",
                currency: note.currency || "EUR",
                fichiers: [],
                commentaire: note.commentaire ?? note.comment ?? "",
            });
        }
    }, [note]);

    const handleChange = (e) => {
        if (!editable) return;

        const { name, value, files } = e.target;
        if (name === "fichiers") {
            const list = files ? Array.from(files) : [];
            setForm((prev) => ({ ...prev, fichiers: list }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!editable || !onSubmit || submitting) return;

        setSubmitting(true);
        try {
            await onSubmit(form);
            setForm({
                date: "",
                type: "Repas",
                montant: "",
                currency: "EUR",
                fichiers: [],
                commentaire: "",
            });
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleValidate = async () => {
        if (!note?.id || !onValidate || actionLoading) return;
        setActionLoading(true);
        try {
            await onValidate(note.id);
            onClose?.();
        } finally {
            setActionLoading(false);
        }
    };

    const handleRefuse = async () => {
        if (!note?.id || !onRefuse || actionLoading) return;
        setActionLoading(true);
        try {
            await onRefuse(note.id, refuseComment.trim() || null);
            setShowRefuseForm(false);
            setRefuseComment("");
            onClose?.();
        } finally {
            setActionLoading(false);
        }
    };

    const handleDownloadDoc = async (noteId, filePath) => {
        setLoadingDoc(filePath);
        try {
            const { getAttachmentUrl } = await import("../../../_api/ExpenseNote");
            const res = await getAttachmentUrl(noteId, filePath);
            const url = res?.data?.url;
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingDoc(null);
        }
    };

    const isPending = note?.status === "PENDING";

    const allAttachments = note
        ? (note.attachments && Array.isArray(note.attachments)
            ? note.attachments.map((a) => ({
                  path: a.path ?? a,
                  name: a.name ?? (typeof a === "string" ? a.split("/").pop() : "Document"),
              }))
            : note.file_path
              ? [{ path: note.file_path, name: note.file_path.split("/").pop() }]
              : [])
        : [];

    const rawHistory = note?.validation_history ?? note?.validationHistory ?? [];
    const hasLegacyValidator = note?.validator && (note?.validated_at || note?.validatedAt);
    const validationHistory =
        rawHistory.length > 0
            ? rawHistory
            : hasLegacyValidator
              ? [
                    {
                        id: "legacy",
                        action: note?.status,
                        user: note?.validator,
                        created_at: note?.validated_at ?? note?.validatedAt,
                        comment: null,
                    },
                ]
              : [];
    const formatDate = (str) => {
        if (!str) return "-";
        const d = new Date(str);
        return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    };
    const getUserName = (u) => (u ? [u.first_name, u.last_name].filter(Boolean).join(" ") || "-" : "-");

    return (
        <div
            className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-lg z-50 transform transition-transform duration-300 ${
                open ? "translate-x-0" : "translate-x-full"
            }`}
        >
            <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold">
                    {note
                        ? editable
                            ? "Modifier la note"
                            : "Détails de la note"
                        : "Nouvelle note"}
                </h2>
                <Button
                    name="×"
                    action={onClose}
                    type="custom"
                    title="Fermer"
                    customCN="cursor-pointer text-gray-500 hover:text-gray-700 text-xl font-bold leading-none px-2 py-1 focus:outline-hidden focus:shadow-outline"
                />
            </div>

            {noteLoading && (
                <div className="p-4 text-center text-gray-500 text-sm">
                    Chargement des détails...
                </div>
            )}
            {!noteLoading && (
            <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-120px)]">
                <div>
                    <label className="block text-sm font-medium mb-1">
                        Date
                    </label>
                    <input
                        type="date"
                        name="date"
                        value={form.date}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-sm p-2"
                        required
                        disabled={!editable}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">
                        Type de dépense
                    </label>
                    <select
                        name="type"
                        value={form.type}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-sm p-2"
                        disabled={!editable}
                    >
                        <option value="Repas">Repas</option>
                        <option value="Transport">Transport</option>
                        <option value="Hôtel">Hôtel</option>
                        <option value="Fournitures">Fournitures</option>
                        <option value="Autre">Autre</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">
                        Devise
                    </label>
                    <select
                        name="currency"
                        value={form.currency}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-sm p-2"
                        disabled={!editable}
                    >
                        {Object.entries(CURRENCIES).map(([code, label]) => (
                            <option key={code} value={code}>
                                {label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">
                        Montant
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            name="montant"
                            value={form.montant}
                            onChange={handleChange}
                            step="0.01"
                            className="w-full border border-gray-300 rounded-sm p-2"
                            required
                            disabled={!editable}
                        />
                        <span className="text-gray-600 font-medium shrink-0">
                            {getCurrencySymbol(form.currency)}
                        </span>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">
                        Documents justificatifs
                    </label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        name="fichiers"
                        accept="image/*,application/pdf"
                        onChange={handleChange}
                        className="w-full text-sm"
                        disabled={!editable}
                        multiple
                    />
                    {editable && form.fichiers.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                            {form.fichiers.length} fichier(s) sélectionné(s)
                        </p>
                    )}
                    {!editable && allAttachments.length > 0 && (
                        <div className="mt-2 space-y-1">
                            <p className="text-xs font-medium text-gray-600">Documents joints :</p>
                            {allAttachments.map((att) => (
                                <div
                                    key={att.path}
                                    className="flex items-center justify-between text-sm bg-gray-50 rounded px-2 py-1.5"
                                >
                                    <span className="truncate flex-1" title={att.name}>
                                        {att.name}
                                    </span>
                                    <Button
                                        name="Voir"
                                        action={() => handleDownloadDoc(note.id, att.path)}
                                        loading={loadingDoc === att.path}
                                        type={"custom"}
                                        title="Ouvrir le document"
                                        customCN="cursor-pointer text-blue-600 hover:text-blue-800 text-xs font-medium shrink-0 ml-2 px-2 py-1 focus:outline-hidden focus:shadow-outline whitespace-nowrap"
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">
                        Commentaire
                    </label>
                    <textarea
                        name="commentaire"
                        value={form.commentaire}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-sm p-2"
                        rows={3}
                        disabled={!editable}
                    />
                </div>

                {!editable && (validationHistory.length > 0 || hasLegacyValidator) && (
                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-600">Historique des validations</label>
                        <div className="space-y-2 text-sm">
                            {validationHistory.map((h) => (
                                <div
                                    key={h.id}
                                    className={`rounded px-3 py-2 ${
                                        h.action === "ACCEPTED"
                                            ? "bg-green-50 text-green-800"
                                            : "bg-red-50 text-red-800"
                                    }`}
                                >
                                    <span className="font-medium">
                                        {h.action === "ACCEPTED" ? "Validée" : "Refusée"}
                                        {" par "}
                                        {getUserName(h.user)}
                                        {" le "}
                                        {formatDate(h.created_at)}
                                    </span>
                                    {h.comment && (
                                        <p className="mt-1 text-xs opacity-90">{h.comment}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {editable && (
                    <Button
                        name="Enregistrer"
                        buttonType="submit"
                        loading={submitting}
                    />
                )}
                {!editable && isPending && onValidate && onRefuse && !showRefuseForm && (
                    <div className="p-4 border-t flex justify-between gap-2">
                        <Button
                            name="Refuser"
                            action={() => setShowRefuseForm(true)}
                            disabled={actionLoading}
                            type={"red"}
                        />
                        <Button
                            name="Valider"
                            action={handleValidate}
                            loading={actionLoading}
                        />
                    </div>
                )}
                {!editable && isPending && onRefuse && showRefuseForm && (
                    <div className="p-4 border-t space-y-3 bg-red-50 rounded">
                        <label className="block text-sm font-medium text-gray-700">
                            Motif du refus (optionnel)
                        </label>
                        <textarea
                            value={refuseComment}
                            onChange={(e) => setRefuseComment(e.target.value)}
                            placeholder="Indiquez la raison du refus..."
                            className="w-full border border-gray-300 rounded-sm p-2 text-sm"
                            rows={3}
                        />
                        <div className="flex gap-2">
                            <Button
                                name="Annuler"
                                action={() => {
                                    setShowRefuseForm(false);
                                    setRefuseComment("");
                                }}
                                disabled={actionLoading}
                                type={"custom"}
                                customCN="flex-1 cursor-pointer rounded-sm border-2 border-gray-200 bg-gray-200 hover:border-gray-300 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 focus:outline-hidden focus:shadow-outline whitespace-nowrap text-sm"
                            />
                            <Button
                                name="Confirmer le refus"
                                action={handleRefuse}
                                loading={actionLoading}
                                type={"red"}
                                customCN="flex-1 cursor-pointer rounded-sm border-2 border-red-600 bg-red-600 hover:border-red-700 hover:bg-red-700 text-white font-bold py-2 px-4 focus:outline-hidden focus:shadow-outline whitespace-nowrap text-sm"
                            />
                        </div>
                    </div>
                )}
            </form>
            )}
        </div>
    );
};

export default AddNotesFrais;
